/*
 * PFC frame finish gallery — "your photo in every frame".
 *
 * Renders the visitor's photo (or a sample) inside every moulding in the
 * all-mouldings collection, as a grid of small canvases grouped by colour.
 * Each frame is drawn with the moulding's real texture (mitred rails with a
 * clipped texture pattern — ported from pfc-moulding.js). Click a tile → opens
 * that moulding's customiser. No size here — this is a finish previewer.
 *
 * Tiles only paint to screen (never read back), so cross-origin moulding
 * textures render fine even though they taint the tile canvas. The photo
 * handoff dataURL is built on a separate same-origin canvas, so it stays clean.
 */
(function () {
  var root = document.querySelector('[data-pfc-frame-gallery]');
  var cfg = window.PFC_FRAME_GALLERY;
  if (!root || !cfg || !Array.isArray(cfg.tiles) || !cfg.tiles.length) return;

  var HANDOFF_KEY = 'pfc-frame-handoff';
  var SAMPLES = (Array.isArray(cfg.placeholderImages) && cfg.placeholderImages.length)
    ? cfg.placeholderImages.filter(Boolean) : [];
  var state = { img: null, usingPlaceholder: true, fit: 'fill', handoffDataUrl: null, sampleIdx: -1 };
  var tileCanvases = [];     // { canvas, tile }
  var texCache = {};         // textureUrl -> HTMLImageElement

  var elGrid = root.querySelector('[data-grid]');
  var elFile = root.querySelector('[data-file-input]');
  var elUploadBtn = root.querySelector('[data-upload-btn]');
  var elUploadLabel = root.querySelector('[data-upload-label]');
  var elClear = root.querySelector('[data-clear-btn]');
  var elFitToggle = root.querySelector('[data-fit-toggle]');
  var elFitBtns = root.querySelectorAll('[data-fit-mode]');
  var elFitNote = root.querySelector('[data-fit-note]');

  /* ---------- Texture loading (no crossOrigin: display only) ---------- */
  function loadTexture(url, cb) {
    if (!url) { cb(null); return; }
    var c = texCache[url];
    if (c) { if (c.complete) cb(c); else c.addEventListener('load', function () { cb(c); }); return; }
    var im = new Image();
    texCache[url] = im;
    im.onload = function () { cb(im); };
    im.onerror = function () { cb(null); };
    im.src = url;
  }

  /* ---------- Grid + grouping ---------- */
  function buildTile(tile, parent) {
    var a = document.createElement('a');
    a.className = 'pfc-fgal__tile';
    a.href = tile.url || '#';
    a.setAttribute('aria-label', 'Design a ' + (tile.label || 'frame'));
    a.addEventListener('click', stashHandoff);

    var canvas = document.createElement('canvas');
    canvas.className = 'pfc-fgal__tile-canvas';
    a.appendChild(canvas);

    var cap = document.createElement('span');
    cap.className = 'pfc-fgal__tile-label';
    cap.textContent = tile.label || '';
    a.appendChild(cap);

    var cta = document.createElement('span');
    cta.className = 'pfc-fgal__tile-cta';
    cta.textContent = 'Design this →';
    a.appendChild(cta);

    parent.appendChild(a);
    var entry = { canvas: canvas, tile: tile };
    tileCanvases.push(entry);
    // Load texture, then repaint just this tile.
    loadTexture(tile.textureUrl, function () { renderTile(canvas, tile); });
  }

  function buildGrid() {
    elGrid.innerHTML = '';
    tileCanvases = [];
    var order = [], byGroup = {};
    cfg.tiles.forEach(function (tile) {
      var g = tile.group || 'Frames';
      if (!byGroup[g]) { byGroup[g] = []; order.push(g); }
      byGroup[g].push(tile);
    });
    var grouped = order.length > 1;
    elGrid.classList.toggle('pfc-fgal__groups', grouped);
    elGrid.classList.toggle('pfc-fgal__grid', !grouped);

    order.forEach(function (g) {
      var gridEl;
      if (grouped) {
        var sec = document.createElement('section');
        sec.className = 'pfc-fgal__group';
        var h = document.createElement('h3');
        h.className = 'pfc-fgal__group-title';
        h.textContent = g;
        sec.appendChild(h);
        gridEl = document.createElement('div');
        gridEl.className = 'pfc-fgal__grid';
        sec.appendChild(gridEl);
        var more = document.createElement('button');
        more.type = 'button'; more.className = 'pfc-fgal__more';
        more.setAttribute('data-grp-toggle', ''); more.hidden = true;
        more.addEventListener('click', function () {
          sec.dataset.expanded = sec.dataset.expanded === 'true' ? 'false' : 'true';
          clampGroup(sec);
        });
        sec.appendChild(more);
        elGrid.appendChild(sec);
      } else { gridEl = elGrid; }
      byGroup[g].forEach(function (tile) { buildTile(tile, gridEl); });
    });
  }

  function clampGroups() { elGrid.querySelectorAll('.pfc-fgal__group').forEach(clampGroup); }
  function clampGroup(sec) {
    var grid = sec.querySelector('.pfc-fgal__grid');
    var btn = sec.querySelector('[data-grp-toggle]');
    if (!grid) return;
    var tiles = grid.querySelectorAll('.pfc-fgal__tile');
    if (tiles.length < 2) { if (btn) btn.hidden = true; grid.style.maxHeight = ''; grid.style.overflow = ''; return; }
    var top0 = tiles[0].getBoundingClientRect().top;
    var row1 = 0;
    tiles.forEach(function (t) { if (t.getBoundingClientRect().top <= top0 + 2) row1++; });
    var hidden = tiles.length - row1;
    var expanded = sec.dataset.expanded === 'true';
    if (!expanded && hidden > 0) {
      var gridTop = grid.getBoundingClientRect().top;
      grid.style.maxHeight = Math.ceil(tiles[0].getBoundingClientRect().bottom - gridTop) + 'px';
      grid.style.overflow = 'hidden';
    } else { grid.style.maxHeight = ''; grid.style.overflow = ''; }
    if (btn) {
      btn.hidden = hidden <= 0;
      btn.textContent = expanded ? 'Show fewer' : ('Show all ' + tiles.length + ' →');
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  /* ---------- Photo handoff (stash for the moulding customiser) ---------- */
  function stashHandoff() {
    try { if (state.handoffDataUrl && !state.usingPlaceholder) sessionStorage.setItem(HANDOFF_KEY, state.handoffDataUrl); } catch (_) {}
  }
  function buildHandoff(img) {
    state.handoffDataUrl = null;
    try {
      var MAX = 2200;
      var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
      if (!iw || !ih) return;
      var s = Math.min(1, MAX / Math.max(iw, ih));
      var w = Math.max(1, Math.round(iw * s)), h = Math.max(1, Math.round(ih * s));
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      cv.getContext('2d').drawImage(img, 0, 0, w, h);
      state.handoffDataUrl = cv.toDataURL('image/jpeg', 0.9);
    } catch (_) { state.handoffDataUrl = null; }
  }

  /* ---------- Geometry ---------- */
  function tileGeom(tile) {
    var fw = tile.widthMm || 20;
    var op = { w: cfg.opening.w, h: cfg.opening.h };
    if (state.fit === 'fit' && state.img) {
      var iw = state.img.naturalWidth || state.img.width, ih = state.img.naturalHeight || state.img.height;
      if (iw && ih) { var ar = iw / ih, LONG = 160; op = ar >= 1 ? { w: LONG, h: LONG / ar } : { w: LONG * ar, h: LONG }; }
    }
    return { opening: op, outer: { w: op.w + 2 * fw, h: op.h + 2 * fw }, fw: fw };
  }

  /* ---------- Frame renderer (ported from pfc-moulding.js) ---------- */
  function drawCover(ctx, img, dx, dy, dw, dh) {
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    var s = Math.max(dw / iw, dh / ih);
    ctx.save(); ctx.beginPath(); ctx.rect(dx, dy, dw, dh); ctx.clip();
    ctx.drawImage(img, dx + (dw - iw * s) / 2, dy + (dh - ih * s) / 2, iw * s, ih * s);
    ctx.restore();
  }

  function drawFrame(ctx, tex, fw, x0, y0, outerW, outerH, dpr) {
    var hasTex = !!(tex && tex.complete && tex.naturalWidth > 0);
    var frameColour = '#3a2c1d';
    ctx.save();
    ctx.shadowColor = 'rgba(60,45,25,0.22)'; ctx.shadowBlur = 18; ctx.shadowOffsetY = 10;
    ctx.fillStyle = frameColour; ctx.fillRect(x0, y0, outerW, outerH);
    ctx.restore();

    var innerX = x0 + fw, innerY = y0 + fw, innerW = outerW - fw * 2, innerH = outerH - fw * 2;

    function drawRail(ox1, oy1, ox2, oy2, ix1, iy1, ix2, iy2, gradDir, tx, ty, angle) {
      ctx.save();
      ctx.beginPath(); ctx.moveTo(ox1, oy1); ctx.lineTo(ox2, oy2); ctx.lineTo(ix2, iy2); ctx.lineTo(ix1, iy1); ctx.closePath();
      ctx.clip();
      if (hasTex) {
        ctx.translate(tx, ty); ctx.rotate(angle);
        var sc = fw / tex.naturalHeight;
        ctx.scale(sc, sc);
        var pat = ctx.createPattern(tex, 'repeat');
        if (pat) { ctx.fillStyle = pat; var maxDim = Math.max(outerW, outerH) / sc; ctx.fillRect(-maxDim, -2, maxDim * 3, tex.naturalHeight + 4); }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      } else { ctx.fillStyle = frameColour; ctx.fill(); }
      var grad = (gradDir === 'tb') ? ctx.createLinearGradient(ox1, oy1, ix1, iy1) : ctx.createLinearGradient(ox1, oy1, ox2, oy2);
      grad.addColorStop(0, 'rgba(255,255,255,0.09)'); grad.addColorStop(0.4, 'rgba(255,255,255,0.02)'); grad.addColorStop(1, 'rgba(0,0,0,0.10)');
      ctx.fillStyle = grad; ctx.fill();
      ctx.restore();
    }
    drawRail(x0, y0, x0 + outerW, y0, innerX, innerY, innerX + innerW, innerY, 'tb', x0, y0, 0);
    drawRail(x0 + outerW, y0 + outerH, x0, y0 + outerH, innerX + innerW, innerY + innerH, innerX, innerY + innerH, 'tb', x0 + outerW, y0 + outerH, Math.PI);
    drawRail(x0, y0 + outerH, x0, y0, innerX, innerY + innerH, innerX, innerY, 'lr', x0, y0 + outerH, -Math.PI / 2);
    drawRail(x0 + outerW, y0, x0 + outerW, y0 + outerH, innerX + innerW, innerY, innerX + innerW, innerY + innerH, 'lr', x0 + outerW, y0, Math.PI / 2);

    ctx.strokeStyle = 'rgba(0,0,0,0.22)'; ctx.lineWidth = 1; ctx.strokeRect(innerX + 0.5, innerY + 0.5, innerW - 1, innerH - 1);
    return { x: innerX, y: innerY, w: innerW, h: innerH };
  }

  function renderTile(canvas, tile) {
    var ctx = canvas.getContext('2d'); if (!ctx) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = Math.max(120, Math.round(canvas.clientWidth || 220));
    var H = Math.max(120, Math.round(canvas.clientHeight || 280));
    if (canvas.width !== W * dpr || canvas.height !== H * dpr) { canvas.width = W * dpr; canvas.height = H * dpr; }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    var bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, '#f9f4ec'); bg.addColorStop(1, '#f1e9da');
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

    var g = tileGeom(tile);
    var scale = Math.min(W / g.outer.w, H / g.outer.h) * 0.86;
    var outerW = g.outer.w * scale, outerH = g.outer.h * scale;
    var x0 = (W - outerW) / 2, y0 = (H - outerH) / 2;
    var fwpx = g.fw * scale;

    var inner = drawFrame(ctx, texCache[tile.textureUrl], fwpx, x0, y0, outerW, outerH, dpr);

    if (state.img) drawCover(ctx, state.img, inner.x, inner.y, inner.w, inner.h);
    else { ctx.fillStyle = '#f0ece3'; ctx.fillRect(inner.x, inner.y, inner.w, inner.h); }
  }

  function renderAll() { tileCanvases.forEach(function (t) { renderTile(t.canvas, t.tile); }); }

  /* ---------- Image loading + sample cycling ---------- */
  function loadImage(src, isPlaceholder) {
    var img = new Image();
    img.onload = function () {
      state.img = img; state.usingPlaceholder = !!isPlaceholder;
      if (isPlaceholder) state.handoffDataUrl = null; else buildHandoff(img);
      renderAll(); updateControls();
      try { window.dispatchEvent(new CustomEvent('pfc-frame-sample', { detail: { src: src, isPlaceholder: !!isPlaceholder } })); } catch (_) {}
    };
    img.onerror = function () { state.img = null; renderAll(); };
    if (!isPlaceholder) img.crossOrigin = 'anonymous';
    img.src = src;
  }
  function loadSample(mode) {
    if (!SAMPLES.length) return;
    if (mode === 'next') state.sampleIdx = (state.sampleIdx + 1) % SAMPLES.length;
    else state.sampleIdx = SAMPLES.length > 1 ? Math.floor(Math.random() * SAMPLES.length) : 0;
    loadImage(SAMPLES[state.sampleIdx], true);
  }

  function noteFor(mode) {
    return mode === 'fit'
      ? 'Fit: the frame is sized to your photo’s shape — nothing cropped. Pick your size when you design.'
      : 'Fill: your photo is cropped to a standard portrait. Fine-tune the crop when you design.';
  }
  function updateControls() {
    if (elUploadLabel) elUploadLabel.textContent = state.usingPlaceholder ? 'Upload your photo' : 'Change photo';
    if (elClear) {
      if (SAMPLES.length) {
        elClear.hidden = false;
        elClear.textContent = (state.usingPlaceholder && SAMPLES.length > 1) ? 'Try another sample' : 'Use sample photo';
        elClear.classList.toggle('is-active', state.usingPlaceholder);
      } else {
        elClear.hidden = state.usingPlaceholder || !state.img;
        elClear.textContent = 'Remove photo';
      }
    }
    var hasImg = !!state.img;
    if (elFitToggle) elFitToggle.hidden = !hasImg;
    if (elFitNote) { elFitNote.textContent = noteFor(state.fit); elFitNote.hidden = !hasImg; }
  }
  function setFit(mode) {
    state.fit = (mode === 'fit') ? 'fit' : 'fill';
    if (elFitBtns) elFitBtns.forEach(function (b) {
      var on = b.getAttribute('data-fit-mode') === state.fit;
      b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (elFitNote) elFitNote.textContent = noteFor(state.fit);
    renderAll();
  }

  /* ---------- Wiring ---------- */
  if (elUploadBtn) elUploadBtn.addEventListener('click', function () { elFile && elFile.click(); });
  if (elFile) elFile.addEventListener('change', function () {
    var f = elFile.files && elFile.files[0]; if (!f) return;
    loadImage(URL.createObjectURL(f), false); elFile.value = '';
  });
  if (elClear) elClear.addEventListener('click', function () {
    if (SAMPLES.length) loadSample('next');
    else { state.img = null; state.usingPlaceholder = true; renderAll(); updateControls(); }
  });
  if (elFitBtns) elFitBtns.forEach(function (b) { b.addEventListener('click', function () { setFit(b.getAttribute('data-fit-mode')); }); });

  var resizeRaf = null;
  window.addEventListener('resize', function () {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(function () { renderAll(); clampGroups(); });
  });
  window.addEventListener('load', clampGroups);

  /* ---------- Init ---------- */
  buildGrid();
  renderAll();
  clampGroups();
  updateControls();
  loadSample('random');
})();
